import * as React from 'react'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listClients, listClientFacilities, inviteUserToClient } from '@/features/admin/api'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { FormField } from '@/components/primitives/FormField'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import '../ManagePages.css'

interface Props {
  open: boolean
  onClose: () => void
  defaultTenantId?: string
}

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Admin (Super Admin)',
  NURSE: 'Nurse',
  VIEWER: 'Viewer',
}

export function InviteUserDrawer({ open, onClose, defaultTenantId }: Props): React.JSX.Element {
  const toast = useToast()
  const qc = useQueryClient()

  const [tenantId, setTenantId] = useState(defaultTenantId ?? '')
  const [facilityId, setFacilityId] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'NURSE' | 'VIEWER' | 'SUPER_ADMIN'>('NURSE')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const clientsQuery = useQuery({ queryKey: ['clients'], queryFn: listClients, enabled: open })
  const clients = clientsQuery.data ?? []

  const facilitiesQuery = useQuery({
    queryKey: ['client-facilities', tenantId],
    queryFn: () => listClientFacilities(tenantId),
    enabled: !!tenantId,
  })
  const facilities = facilitiesQuery.data ?? []

  function reset(): void {
    setTenantId(defaultTenantId ?? '')
    setFacilityId(''); setName(''); setEmail('')
    setRole('NURSE'); setFieldErrors({})
  }

  const invite = useMutation({
    mutationFn: () =>
      inviteUserToClient(tenantId, {
        name: name.trim(),
        email: email.trim(),
        role,
        facility_id: facilityId || undefined,
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['admin-users'] })
      reset()
      onClose()
      if (!res.email_sent) {
        toast.warning('User created — email failed', 'Resend the invite manually.')
      } else if (res.invite_url) {
        toast.success('User invited', `Dev link: ${res.invite_url}`)
      } else {
        toast.success('User invited', `Invite sent to ${res.email}.`)
      }
    },
    onError: (err) => {
      if (err instanceof ApiError && err.field) {
        setFieldErrors({ [err.field]: err.message })
      } else {
        toast.error('Could not invite', err instanceof ApiError ? err.message : 'Try again.')
      }
    },
  })

  const canSubmit = tenantId && name.trim() && email.trim()

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Invite user"
      subtitle="Send an invite to add a user to an organization."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={invite.isPending}>Cancel</Button>
          <Button onClick={() => invite.mutate()} loading={invite.isPending} disabled={!canSubmit}>
            Send invite
          </Button>
        </>
      }
    >
      <div className="drawer-sections">
        <div className="drawer-section">
          <div className="drawer-section-label">Organization &amp; facility</div>
          <FormField label="Organization" htmlFor="iu-org" required>
            <select
              id="iu-org"
              className="select"
              value={tenantId}
              onChange={e => { setTenantId(e.target.value); setFacilityId('') }}
            >
              <option value="">— choose an organization —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </FormField>
          {tenantId ? (
            <FormField label="Facility (optional)" htmlFor="iu-fac">
              <select
                id="iu-fac"
                className="select"
                value={facilityId}
                onChange={e => setFacilityId(e.target.value)}
                disabled={facilitiesQuery.isLoading}
              >
                <option value="">— no specific facility —</option>
                {facilities.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </FormField>
          ) : null}
        </div>

        <div className="drawer-section">
          <div className="drawer-section-label">User details</div>
          <div className="drawer-2col">
            <FormField label="Full name" htmlFor="iu-name" required error={fieldErrors.name}>
              <Input id="iu-name" value={name} onChange={e => { setName(e.target.value); setFieldErrors(p => ({ ...p, name: '' })) }} placeholder="Jane Smith" />
            </FormField>
            <FormField label="Role" htmlFor="iu-role" required>
              <select
                id="iu-role"
                className="select"
                value={role}
                onChange={e => setRole(e.target.value as typeof role)}
              >
                {Object.entries(ROLE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </FormField>
          </div>
          <FormField label="Email" htmlFor="iu-email" required error={fieldErrors.email}>
            <Input id="iu-email" type="email" value={email} onChange={e => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: '' })) }} placeholder="jane@facility.com" />
          </FormField>
        </div>
      </div>
    </Drawer>
  )
}

